import React, { useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';

interface RedirectRule {
  from: RegExp;
  to: string;
  type: 301 | 302;
  condition?: () => boolean;
}

export class RedirectManager {
  private rules: RedirectRule[] = [
    // Remove trailing slashes (except root)
    {
      from: /^(.+)\/$/,
      to: '$1',
      type: 301
    },
    
    // Old URL patterns
    {
      from: /^\/location\/(.+)$/,
      to: '/locations/$1',
      type: 301
    },
    {
      from: /^\/service\/(.+)$/,
      to: '/services/$1',
      type: 301
    },
    
    // Common homepage variations
    {
      from: /^\/home\/?$/,
      to: '/',
      type: 301
    },
    {
      from: /^\/index\/?$/,
      to: '/',
      type: 301
    },
    {
      from: /^\/index\.html?$/,
      to: '/',
      type: 301
    },
    
    // Case sensitivity fixes
    {
      from: /^\/Blog\/(.+)$/,
      to: '/blog/$1',
      type: 301
    },
    {
      from: /^\/Locations\/(.+)$/,
      to: '/locations/$1',
      type: 301
    },
    {
      from: /^\/Services\/(.+)$/,
      to: '/services/$1',
      type: 301
    },
    
    // Remove query parameters (except UTM and necessary ones)
    {
      from: /^(.+)\?(?!utm_|ref=|source=).*$/,
      to: '$1',
      type: 301
    },
    
    // Consolidate duplicate pages
    {
      from: /^\/contact-us\/?$/,
      to: '/contact',
      type: 301
    },
    {
      from: /^\/about-us\/?$/,
      to: '/about',
      type: 301
    },
    {
      from: /^\/get-quote\/?$/,
      to: '/quote',
      type: 301
    },
    {
      from: /^\/free-estimate\/?$/,
      to: '/quote',
      type: 301
    },
    
    // Location variations
    {
      from: /^\/locations\/rocky-mount-nc\/?$/,
      to: '/locations/rocky-mount',
      type: 301
    },
    {
      from: /^\/locations\/raleigh-nc\/?$/,
      to: '/locations/raleigh',
      type: 301
    },
    {
      from: /^\/locations\/greenville-nc\/?$/,
      to: '/locations/greenville',
      type: 301
    }
  ];

  checkForRedirect(currentPath: string): { shouldRedirect: boolean; redirectTo?: string; type?: number } {
    for (const rule of this.rules) {
      const match = currentPath.match(rule.from);
      
      if (match) {
        // Check condition if present
        if (rule.condition && !rule.condition()) {
          continue;
        }
        
        let redirectTo = rule.to;
        
        // Replace captured groups
        match.slice(1).forEach((group, index) => {
          redirectTo = redirectTo.replace(`$${index + 1}`, group);
        });
        
        // Don't redirect to the same URL
        if (redirectTo === currentPath) {
          continue;
        }
        
        return {
          shouldRedirect: true,
          redirectTo,
          type: rule.type
        };
      }
    }
    
    return { shouldRedirect: false };
  }

  addRule(rule: RedirectRule): void {
    this.rules.unshift(rule); // Add to beginning for priority
  }

  generateNginxRules(): string {
    const nginxRules = this.rules.map(rule => {
      const pattern = rule.from.source
        .replace(/\\\//g, '/')
        .replace(/\$\$/, '$')
        .replace(/\^/, '')
        .replace(/\$$/, '');
      
      return `rewrite ^${pattern}$ ${rule.to} permanent;`;
    });

    return nginxRules.join('\n');
  }

  generateApacheRules(): string {
    const apacheRules = this.rules.map(rule => {
      const pattern = rule.from.source
        .replace(/\\\//g, '/')
        .replace(/\$\$/, '$')
        .replace(/\^/, '^')
        .replace(/\$$/, '$');
      
      const flags = rule.type === 301 ? 'R=301,L' : 'R=302,L';
      return `RewriteRule ${pattern} ${rule.to} [${flags}]`;
    });

    return `RewriteEngine On\n${apacheRules.join('\n')}`;
  }
}

export const redirectManager = new RedirectManager();

// React component for client-side redirects
export const RedirectHandler = () => {
  const location = useLocation();
  
  const redirectCheck = redirectManager.checkForRedirect(location.pathname + location.search);
  
  if (redirectCheck.shouldRedirect && redirectCheck.redirectTo) {
    return React.createElement(Navigate, { to: redirectCheck.redirectTo, replace: true });
  }
  
  return null;
};

// Hook for programmatic redirects
export const useRedirectCheck = () => {
  const location = useLocation();
  
  useEffect(() => {
    const redirectCheck = redirectManager.checkForRedirect(location.pathname + location.search);
    
    if (redirectCheck.shouldRedirect && redirectCheck.redirectTo) {
      // For SEO purposes, log the redirect
      console.log(`Redirect ${redirectCheck.type}: ${location.pathname} -> ${redirectCheck.redirectTo}`);
      
      // You could also send this data to analytics
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'redirect', {
          from: location.pathname,
          to: redirectCheck.redirectTo,
          type: redirectCheck.type
        });
      }
    }
  }, [location]);
};

// Component to inject into App.tsx
export const GlobalRedirectHandler = () => {
  useRedirectCheck();
  return React.createElement(RedirectHandler);
};